import { Flex, Button, useToast, IconButton, Text } from '@chakra-ui/react';
import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  SubmitHandler,
  useForm,
  FieldError,
  FieldValues,
} from 'react-hook-form';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/Form/Input';
import useAuth from '../../../hooks/useAuth';

type FormData = {
  password: string;
  passwordConfirmation: string;
  email: string;
  token: string;
};

const formSchema = yup.object().shape({
  password: yup
    .string()
    .required('Campo obligatorio')
    .min(8, 'La contraseña debe contener al menos 8 caracteres')
    .max(15, 'La contraseña debe contener como máximo 15 caracteres'),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Las contraseñas deben coincidir')
    .required('Campo obligatorio'),
});

const ResetPassword: React.FC = () => {
  const toast = useToast();
  const { resetPassword } = useAuth();
  const history = useHistory();

  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(formSchema),
  });

  const { errors } = formState;
  const [show, setShow] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      Object.assign(data, {
        token: new URLSearchParams(window.location.search).get('token'),
        email: new URLSearchParams(window.location.search).get('email'),
      });
      await resetPassword(data);
      toast({
        title: t('success'),
        description: t('passwordRedefined'),
        status: 'success',
        position: 'top-right',
        duration: 5000,
      });
      history.push('/');
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.response?.data?.message || err?.message || t('sendEmailError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Flex w="100vw" h="100vh">
      <Flex flex={1} h="100vh" justify="center" align="center" bg="gray.600" />
      <Flex
        as="form"
        h="100vh"
        w={500}
        justify="center"
        p={42}
        flexDir="column"
        gridGap={3}
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <Text fontSize="xl" textAlign="center" mb={3} fontWeight="semibold">
          {t('newPassword')}
        </Text>
        <Input
          label={t('passwordField')}
          type={show ? 'text' : 'password'}
          error={errors.password as FieldError | undefined}
          InputRightElement={
            <IconButton
              variant="ghost"
              colorScheme="transparent"
              aria-label="Esconder ou mostrar a senha"
              icon={show ? <HiEye /> : <HiEyeOff />}
              onClick={() => setShow(state => !state)}
            />
          }
          {...register('password')}
        />
        <Input
          label={t('passwordConfirmationField')}
          type={showConfirmationPassword ? 'text' : 'password'}
          error={errors.passwordConfirmation as FieldError | undefined}
          InputRightElement={
            <IconButton
              variant="ghost"
              colorScheme="transparent"
              aria-label="Esconder ou mostrar a senha"
              icon={showConfirmationPassword ? <HiEye /> : <HiEyeOff />}
              onClick={() => setShowConfirmationPassword(state => !state)}
            />
          }
          onClick={() => setShow(state => !state)}
          {...register('passwordConfirmation')}
        />

        <Button
          type="submit"
          mt={10}
          colorScheme="brand"
          size="md"
          isLoading={loading}
        >
          {t('send')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default ResetPassword;
